import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Users, UserPlus, CheckCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

interface TherapistConnectionCardProps {
  userId: string;
}

export function TherapistConnectionCard({ userId }: TherapistConnectionCardProps) {
  const [hasConnection, setHasConnection] = useState(false);
  const [connectionCount, setConnectionCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (userId) {
      checkConnections();
    }
  }, [userId]);

  const checkConnections = async () => {
    try {
      const { data, error } = await supabase
        .from('clinician_clients')
        .select('id, status')
        .eq('client_id', userId)
        .eq('status', 'active');

      if (!error && data) {
        setConnectionCount(data.length);
        setHasConnection(data.length > 0);
      }
    } catch (error) {
      console.error('Error checking therapist connections:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="p-4 sm:p-6 shadow-therapy border-2 border-primary/30 hover:border-primary/60 hover:shadow-gentle transition-all duration-300 hover:scale-105 flex flex-col bg-gradient-to-br from-background to-primary/5 animate-pulse">
        <div className="h-10 bg-muted rounded mb-4" />
        <div className="h-4 bg-muted rounded mb-2" />
        <div className="h-4 bg-muted rounded w-3/4 mb-4" />
        <div className="h-10 bg-muted rounded" />
      </Card>
    );
  }

  return (
    <Card className="p-4 sm:p-6 shadow-therapy border-2 border-primary/30 hover:border-primary/60 hover:shadow-gentle transition-all duration-300 hover:scale-105 flex flex-col bg-gradient-to-br from-background to-primary/5">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-gradient-primary rounded-full flex items-center justify-center">
          <Users className="h-5 w-5 text-primary-foreground" />
        </div>
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-light text-foreground">My Therapist</h3>
          {hasConnection && (
            <Badge variant="secondary" className="text-xs">
              <CheckCircle className="h-3 w-3 mr-1" />
              Connected
            </Badge>
          )}
        </div>
      </div>
      <p className="text-muted-foreground mb-4 text-sm flex-1">
        {hasConnection 
          ? `You have ${connectionCount} therapist connection${connectionCount > 1 ? 's' : ''}. Share your wellbeing data for better support.`
          : "Connect with your therapist to share your wellbeing data and receive better support."
        }
      </p>
      <Link to="/connect-and-grow#therapist">
        <Button className="w-full" variant={hasConnection ? "outline" : "default"}>
          {hasConnection ? (
            <>
              <Users className="h-4 w-4 mr-2" />
              View Connections
            </>
          ) : (
            <>
              <UserPlus className="h-4 w-4 mr-2" />
              Connect Therapist
            </>
          )}
        </Button>
      </Link>
    </Card>
  );
}
